import { apiClient, getApiUrl } from '../lib/apiClient';
import type { 
  Detection,
  DetectionQueryParams,
  Pagination,
  ApiResponse
} from '../types/api';
import { 
  extractListData, 
  extractSingleData, 
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse 
} from '../utils/railsResponseHandler';

// Detection operations
export const getDetections = async (params?: DetectionQueryParams): Promise<{ detections: Detection[]; pagination: Pagination }> => {
  const response = await apiClient.get<RailsWrappedListResponse<Detection>>(getApiUrl('/detections'), { params });
  
  const detections = extractListData<Detection>(response.data);
  const pagination = extractPagination(response.data) || {
    current_page: 1,
    total_pages: 1,
    total_count: detections.length,
    per_page: 20,
    has_previous: false,
    has_next: false
  };
  
  return { detections, pagination };
};

export const getDetection = async (id: string, view?: string): Promise<Detection> => {
  const params = view ? { view } : {};
  const response = await apiClient.get<RailsSingleWrappedResponse<Detection>>(getApiUrl(`/detections/${id}`), { params });
  return extractSingleData<Detection>(response.data);
};

export const updateDetection = async (id: string, data: { 
  verified?: boolean; // OpenAPI field
  verification_notes?: string; // OpenAPI field
}): Promise<Detection> => {
  const response = await apiClient.patch<RailsSingleWrappedResponse<Detection>>(getApiUrl(`/detections/${id}`), data);
  return extractSingleData<Detection>(response.data);
};

export const bulkUpdateDetections = async (data: {
  detection_ids: string[];
  verification_status: 'verified' | 'false_positive' | 'needs_review';
  notes?: string;
}): Promise<ApiResponse<Detection[]>> => {
  const response = await apiClient.post<RailsWrappedListResponse<Detection>>(getApiUrl('/detections/bulk_update'), data);
  const detections = extractListData<Detection>(response.data);
  return { data: detections } as ApiResponse<Detection[]>;
};

export const getDetectionStats = async (params?: {
  start_date?: string;
  end_date?: string;
  school_id?: number;
  camera_id?: number;
  student_id?: number;
}): Promise<ApiResponse<any>> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<any>>(getApiUrl('/detections/stats'), { params });
  const stats = extractSingleData<any>(response.data);
  return { data: stats } as ApiResponse<any>;
};